import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {camelCaseToLowerSentenceCase, camelCaseToSentenceCase, capitalize} from '../store/transformation.helpers';
import {MatIcon} from '@angular/material/icon';
import {FormatPipe} from '../pipes/format.pipe';
import {CharacterMaskPipe} from '@aviary/pipes/character-mask.pipe';
import {YesOrNoPipe} from '@aviary/pipes/yes-or-no.pipe';
import {ReplaceEmptyPipe} from '@aviary/pipes/replace-empty.pipe';
import {PhonePipe} from '@aviary/pipes/phone.pipe';
import {CurrencyPipe, DatePipe, DecimalPipe, PercentPipe, TitleCasePipe as AngularTitleCasePipe, UpperCasePipe} from '@angular/common';
import {TitleCasePipe} from '@aviary/pipes/title-case.pipe';
import {MAXIMUM_PIN_LENGTH, MINIMUM_PIN_LENGTH} from '@aviary/store/common/common.validators';

@Component({
  selector: 'mx-field-error',
  host: {
    class: 'flex flex-row gap-x-[0.161rem]'
  },
  template: `
    @if (field && field.invalid) {
      <mat-icon class="mat-icon-size-3.5 material-icons-outlined shrink-0">warning</mat-icon>
      <span>{{ getErrorMessage() }}</span>
    }
  `,
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- Async validator error messages fail to show with OnPush.
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [
    FormatPipe,
    AngularTitleCasePipe, CurrencyPipe, DatePipe, DecimalPipe, PercentPipe, UpperCasePipe, // angular pipes
    CharacterMaskPipe, PhonePipe, ReplaceEmptyPipe, TitleCasePipe, YesOrNoPipe // mx pipes
  ],
  imports: [MatIcon]
})
export class FieldErrorComponent {
  private readonly formatPipe: FormatPipe = inject(FormatPipe);
  @Input() field: AbstractControl;
  @Input() fieldLabel: string;

  public getErrorMessage(): string {
    const lowercaseLabel: string = this.fieldLabel?.toLowerCase() ?? '';
    const capitalizedLabel: string = capitalize(lowercaseLabel);

    if (this.field.hasError('required')) {
      return `${capitalizedLabel} is required.`;
    }

    if (this.field.hasError('maxlength')) {
      return `${capitalizedLabel} is too long. Please use no more than ${this.field.getError('maxlength').requiredLength} characters.`;
    }

    if (this.field.hasError('minlength')) {
      return `${capitalizedLabel} is too short. Please use at least ${this.field.getError('minlength').requiredLength} characters.`;
    }

    if (this.field.hasError('exactLength')) {
      return `${capitalizedLabel} must contain exactly ${this.field.getError('exactLength').requiredLength} characters.`;
    }

    if (this.field.hasError('max')) {
      return `${capitalizedLabel} must be no more than ${this.field.getError('max').max}.`;
    }

    if (this.field.hasError('min')) {
      return `${capitalizedLabel} must be at least ${this.field.getError('min').min}.`;
    }

    if (this.field.hasError('currencyMax')) {
      return `${capitalizedLabel} must be no more than ${this.formatPipe.transform(this.field.getError('currencyMax').max, 'CURRENCY')}.`;
    }

    if (this.field.hasError('currencyMin')) {
      return `${capitalizedLabel} must be at least ${this.formatPipe.transform(this.field.getError('currencyMin').min, 'CURRENCY')}.`;
    }

    if (this.field.hasError('minPayment')) {
      return `${this.formatPipe.transform(this.field.getError('minPayment').min, 'CURRENCY')} minimum payment required.`;
    }

    if (this.field.hasError('requiresUppercase') || this.field.hasError('requiresLowercase')) {
      return `${capitalizedLabel} must contain at least one uppercase and one lowercase letter.`;
    }

    if (this.field.hasError('requiresDigit') || this.field.hasError('requiresSpecialChars')) {
      return `${capitalizedLabel} must contain at least one symbol and one number.`;
    }

    if (this.field.hasError('passwordMismatch')) {
      return 'Passwords do not match.';
    }

    if (this.field.hasError('credentialMismatch')) {
      return this.field.getError('credentialMismatch');
    }

    if (this.field.hasError('incorrectCurrentPassword')) {
      return 'Password is incorrect.';
    }

    if (this.field.hasError('incorrectCurrentPin')) {
      return 'PIN is incorrect.';
    }

    if (this.field.hasError('mustDifferFromCurrentPin')) {
      return 'New PIN must be different from current PIN.';
    }

    if (this.field.hasError('mustBeTenDigitPhoneNumber')) {
      return `${capitalizedLabel} must be a 10-digit number.`;
    }

    if (this.field.hasError('mustBeValidPhoneNumber')) {
      return `${capitalizedLabel} is invalid. Please use a 10-digit number with a valid area code and exchange.`;
    }

    if (this.field.hasError('pinCode')) {
      return `PIN is invalid. PINs should be numeric and contain between ${MINIMUM_PIN_LENGTH} and ${MAXIMUM_PIN_LENGTH} digits.`;
    }

    if (this.field.hasError('verificationCode')) {
      return 'Verification code is invalid. Please use a 6-digit verification code.'
    }

    if (this.field.hasError('zipCode')) {
      return 'Zip code is invalid.  Please use a 5-digit (#####) or ZIP+4 (#####-####) code.'
    }

    if (this.field.hasError('currency')) {
      return 'Please use a valid currency value.';
    }

    if (this.field.hasError('email')) {
      return 'Please use a valid email address.';
    }

    if (this.field.hasError('userEmailUnavailable') ||
      this.field.hasError('patronEmailUnavailable')) {
      return 'This email address is unavailable. Please use a different email.';
    }

    if (this.field.hasError('patronBarcodeUnavailable')) {
      return 'This barcode is unavailable. Please use a different barcode.';
    }

    if (this.field.hasError('mustBePastDate')) {
      return `${capitalizedLabel} must be in the past.`;
    }

    if (this.field.hasError('cannotBePastDate')) {
      return `${capitalizedLabel} cannot be in the past.`;
    }

    if (this.field.hasError('mustBeFutureDate')) {
      return `${capitalizedLabel} must be in the future.`;
    }

    if (this.field.hasError('cannotBeFutureDate')) {
      return `${capitalizedLabel} cannot be in the future.`;
    }

    if (this.field.hasError('cannotBeBeforeOldDate')) {
      return `${capitalizedLabel} cannot be set earlier than the previous ${lowercaseLabel}.`;
    }

    if (this.field.hasError('cannotBeBeforeDate')) {
      return `${capitalizedLabel} cannot be before ${this.field.getError('cannotBeBeforeDate').dateDescription}.`;
    }

    if (this.field.hasError('cannotBeAfterDate')) {
      return `${capitalizedLabel} cannot be after ${this.field.getError('cannotBeAfterDate').dateDescription}.`;
    }

    if (this.field.hasError('mustBeValidRecordSet')) {
      return `Look up the name of an existing record set here.`;
    }

    if (this.field.hasError('mustBeInteger')) {
      return `${capitalizedLabel} must be an integer.`;
    }

    if (this.field.hasError('mustBeDecimal')) {
      return `${capitalizedLabel} must be a number.`;
    }

    if (this.field.hasError('mustBePositiveInteger')) {
      return `${capitalizedLabel} must be a natural number.`;
    }

    if (this.field.hasError('mustBeNonNegativeInteger')) {
      return `${capitalizedLabel} must be a whole number.`;
    }

    if (this.field.hasError('queueIndexMustBeNumeric')) {
      return `${capitalizedLabel} must be a positive number.`;
    }

    if (this.field.hasError('barcodeNotUnique')) {
      return 'This barcode is unavailable. Please use a different barcode.';
    }

    if (this.field.hasError('marcYearRangeMustBeLessThanOrEqualTo')) {
      const error: Record<string, string> = this.field.getError('marcYearRangeMustBeLessThanOrEqualTo');
      return `${camelCaseToSentenceCase(error['fieldName'])} must be greater than or equal to ${camelCaseToLowerSentenceCase(error['dependencyFieldName'])}.`;
    }

    if (this.field.hasError('marcYearRangeMustBeLessThan')) {
      const error: Record<string, string> = this.field.getError('marcYearRangeMustBeLessThan');
      return `${camelCaseToSentenceCase(error['fieldName'])} must be greater than ${camelCaseToLowerSentenceCase(error['dependencyFieldName'])}.`;
    }

    if (this.field.hasError('hasInvalidSpace')) {
      return `${capitalizedLabel} cannot contain spaces.`;
    }

    if (this.field.hasError('smsSelectedButInvalid')) {
      return 'A valid SMS number is required for SMS Notifications.';
    }

    if (this.field.hasError('emailSelectedButInvalid')) {
      return 'A valid Email address is required for Email Notifications.';
    }

    if (this.field.hasError('phoneSelectedButInvalid')) {
      return 'A valid phone number is required for Phone Notifications.';
    }

    if (this.field.hasError('smsConsentRequired')) {
      return 'SMS consent required.';
    }

    return `Please enter a valid ${lowercaseLabel}.`;
  }
}
